<script>
// NOTICE: KAPTION-INTEGRATION
export default {
  props: {
    policy: {
      type: Object,
      required: true,
    },
  },
  emits: ['edit'],
  computed: {
    thresholds() {
      const items = [];
      if (this.policy.first_response_time_threshold) {
        items.push({
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.FIRST_RESPONSE'),
          value: this.formatThreshold(
            this.policy.first_response_time_threshold
          ),
        });
      }
      if (this.policy.next_response_time_threshold) {
        items.push({
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.NEXT_RESPONSE'),
          value: this.formatThreshold(this.policy.next_response_time_threshold),
        });
      }
      if (this.policy.resolution_time_threshold) {
        items.push({
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.RESOLUTION'),
          value: this.formatThreshold(this.policy.resolution_time_threshold),
        });
      }
      return items;
    },
    labelBadges() {
      const items = [];
      if (this.policy.frt_label) {
        items.push({
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.FIRST_RESPONSE'),
          value: this.policy.frt_label,
        });
      }
      if (this.policy.nrt_label) {
        items.push({
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.NEXT_RESPONSE'),
          value: this.policy.nrt_label,
        });
      }
      if (this.policy.rt_label) {
        items.push({
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.RESOLUTION'),
          value: this.policy.rt_label,
        });
      }
      return items;
    },
    modeBadge() {
      const mode = this.policy.mode || 'always';
      const badges = {
        always: {
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.MODE_ALWAYS'),
          bgClass: 'bg-b-alpha-2',
          textClass: 'text-b-solid',
          icon: 'i-lucide-clock',
        },
        business_hours: {
          label: this.$t('KAPTION_RESPONSE_POLICIES.CARD.MODE_BUSINESS_HOURS'),
          bgClass: 'bg-s-alpha-2',
          textClass: 'text-s-solid',
          icon: 'i-lucide-briefcase',
        },
        outside_business_hours: {
          label: this.$t(
            'KAPTION_RESPONSE_POLICIES.CARD.MODE_OUTSIDE_BUSINESS_HOURS'
          ),
          bgClass: 'bg-w-alpha-2',
          textClass: 'text-w-solid',
          icon: 'i-lucide-moon',
        },
      };
      return badges[mode] || badges.always;
    },
  },
  methods: {
    formatThreshold(seconds) {
      if (!seconds) return this.$t('KAPTION_RESPONSE_POLICIES.CARD.NOT_SET');

      const totalSeconds = Number(seconds);
      if (totalSeconds >= 86400) {
        const days = totalSeconds / 86400;
        return days === 1 ? '1 day' : `${days} days`;
      }
      if (totalSeconds >= 3600) {
        const hours = totalSeconds / 3600;
        return hours === 1 ? '1 hour' : `${hours} hours`;
      }
      const minutes = totalSeconds / 60;
      return minutes === 1 ? '1 minute' : `${minutes} minutes`;
    },
  },
};
</script>

<template>
  <div
    class="group flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4 transition-shadow hover:shadow-sm"
  >
    <div class="flex items-start justify-between">
      <div class="flex flex-col gap-1">
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ policy.name }}
        </h3>
        <p v-if="policy.description" class="text-xs text-n-slate-11">
          {{ policy.description }}
        </p>
      </div>
      <div
        class="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <button
          class="flex items-center justify-center size-7 rounded-md text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
          @click="$emit('edit', policy)"
        >
          <span class="i-lucide-pencil size-3.5" />
        </button>
      </div>
    </div>

    <div class="flex flex-wrap gap-2">
      <span
        v-for="threshold in thresholds"
        :key="threshold.label"
        class="inline-flex items-center gap-1.5 rounded-lg bg-n-alpha-2 px-2.5 py-1 text-xs"
      >
        <span class="font-medium text-n-slate-11">{{ threshold.label }}:</span>
        <span class="font-semibold text-n-slate-12">{{ threshold.value }}</span>
      </span>
      <span
        class="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs"
        :class="modeBadge.bgClass"
      >
        <span class="size-3" :class="[modeBadge.icon, modeBadge.textClass]" />
        <span class="font-medium" :class="modeBadge.textClass">
          {{ modeBadge.label }}
        </span>
      </span>
    </div>

    <div v-if="labelBadges.length" class="flex flex-wrap gap-2">
      <span
        v-for="badge in labelBadges"
        :key="badge.label"
        class="inline-flex items-center gap-1.5 rounded-lg bg-r-alpha-2 px-2.5 py-1 text-xs"
      >
        <span class="i-lucide-tag size-3 text-r-solid" />
        <span class="font-medium text-r-solid">
          {{ badge.label }}: {{ badge.value }}
        </span>
      </span>
      <span
        v-if="policy.label_expiry_days != null && policy.label_expiry_days > 0"
        class="inline-flex items-center gap-1.5 rounded-lg bg-n-alpha-2 px-2.5 py-1 text-xs"
      >
        <span class="i-lucide-timer size-3 text-n-slate-11" />
        <span class="font-medium text-n-slate-11">
          {{
            $t('KAPTION_RESPONSE_POLICIES.CARD.LABEL_EXPIRY', {
              days: policy.label_expiry_days,
            })
          }}
        </span>
      </span>
    </div>
  </div>
</template>
